/**
 * Theme Decoration System for Flow2
 *
 * Defines visual decoration layers (background images, shape overlays, gradient shapes)
 * that give themes visual atmosphere beyond just colors and fonts.
 *
 * Decorations compile into MMOs placed at the bottom of the z-order (first in
 * mixedMediaObjects array), so content renders on top.
 *
 * @packageDocumentation
 * @module ai/styles
 */

import { ScreenType } from '../schemas/ContentBlock';

/**
 * A single stop in a gradient definition
 */
export interface GradientStop {
  /** Hex color, e.g. "#6366F1" or with alpha "#6366F180" */
  color: string;
  /** Offset as percentage 0-100 */
  offset: number;
}

/**
 * Gradient definition for gradient-shape decorations
 */
export interface GradientDef {
  type: 'linear' | 'radial';
  /** Angle in degrees for linear gradients (0=top-to-bottom, 90=left-to-right) */
  angle?: number;
  /** Position for radial gradients: 'center' | 'upper-left' | 'upper-right' | 'lower-left' | 'lower-right' */
  position?: string;
  /** Overall opacity 0-1 applied to all stops. Defaults to 1. */
  opacity?: number;
  stops: GradientStop[];
}

/**
 * Border definition for shapes, images, and media objects
 */
export interface BorderDef {
  /** Border color (hex, e.g. "#FF0000" or with alpha "#FF000080") */
  color: string;
  /** Border width in pixels */
  width: number;
}

/**
 * A single decoration element that compiles into an MMO
 */
export interface DecorationElement {
  /** Type determines which MMO is generated */
  type: 'image' | 'shape' | 'gradient-shape';
  /** Position and size in pixels on 1024x748 canvas */
  frame: { x: number; y: number; width: number; height: number };

  // Shape properties
  shapeType?: 'rectangle' | 'ellipse' | 'roundedRectangle' | 'isoscelesTriangle' | 'rightTriangle';
  /** Hex color with optional alpha suffix, e.g. "#1E1B4BBF" */
  color?: string;
  /** Rotation (0-3 quarter turns) */
  rotation?: 0 | 1 | 2 | 3;
  /** Corner radius for rounded shapes (pixels) */
  cornerRadius?: number;

  // Border properties (applies to shapes, images, and media objects)
  /** Border definition */
  border?: BorderDef;

  // Image properties
  /** Path to stock photo, e.g. "/include/img/themes/conference/title-bg.jpg" */
  imageUrl?: string;

  // Gradient properties
  gradient?: GradientDef;
}

/**
 * Decoration set for one or more screen types
 */
export interface ScreenDecoration {
  /** Screen types this decoration applies to. undefined = all screens */
  appliesTo?: ScreenType[];
  /** Decoration elements, ordered bottom-to-top (first = furthest back) */
  elements: DecorationElement[];
}

/**
 * Get the merged decoration elements for a given screen type.
 *
 * Merging logic:
 * - Decorations with no `appliesTo` (universal) are included first
 * - Decorations that specifically match the screenType are appended after
 * - This means specific decorations render on top of universal ones
 *
 * Note: 'toc' screen type falls back to 'content' decorations since most themes
 * don't define specific TOC decorations.
 *
 * @param decorations - The theme's decoration definitions
 * @param screenType - The screen type to get decorations for
 * @returns Array of decoration elements in z-order (first = bottom)
 */
export function getDecorationsForScreen(
  decorations: ScreenDecoration[] | undefined,
  screenType: ScreenType
): DecorationElement[] {
  if (!decorations || decorations.length === 0) {
    return [];
  }

  // TOC screens use 'content' decorations as fallback
  const effectiveScreenType = screenType === 'toc' ? 'content' : screenType;

  const elements: DecorationElement[] = [];

  // First: universal decorations (no appliesTo)
  for (const dec of decorations) {
    if (!dec.appliesTo) {
      elements.push(...dec.elements);
    }
  }

  // Then: screen-type-specific decorations
  for (const dec of decorations) {
    if (dec.appliesTo && dec.appliesTo.includes(effectiveScreenType)) {
      elements.push(...dec.elements);
    }
  }

  return elements;
}

/**
 * Parse a hex color string with optional alpha into { hex6, alpha01 }.
 * Supports "#RRGGBB" (alpha=1) and "#RRGGBBAA" (alpha from AA byte).
 */
export function parseDecorationColor(color: string): { hex6: string; alpha: number } {
  const clean = color.startsWith('#') ? color.slice(1) : color;

  if (clean.length === 8) {
    const hex6 = '#' + clean.slice(0, 6);
    const alpha = parseInt(clean.slice(6, 8), 16) / 255;
    return { hex6, alpha };
  }

  return { hex6: '#' + clean.slice(0, 6), alpha: 1 };
}

// =============================================================================
// AUTO-CONFORM SYSTEM
// =============================================================================
// Allows theme decorations to be defined abstractly and conformed to any
// target view size (e.g., 1024x748 for iPad, 1920x1080 for desktop widescreen)

/**
 * Standard view sizes
 */
export const VIEW_SIZES = {
  /** iPad / Standard 4:3 */
  STANDARD: { width: 1024, height: 748 },
  /** Desktop widescreen 16:9 */
  WIDESCREEN: { width: 1920, height: 1080 },
} as const;

export type ViewSize = { width: number; height: number };

/**
 * Anchor points for positioning elements
 */
export type AnchorPoint =
  | 'top-left' | 'top-center' | 'top-right'
  | 'center-left' | 'center' | 'center-right'
  | 'bottom-left' | 'bottom-center' | 'bottom-right'
  | 'stretch'              // Full canvas
  | 'stretch-horizontal'   // Full width, fixed height
  | 'stretch-vertical';    // Full height, fixed width

/**
 * Size specification - can be percentage, pixels, or 'auto' (maintain aspect ratio)
 */
export interface SizeSpec {
  value: number;
  unit: '%' | 'px';
}

/**
 * Abstract decoration element that can be conformed to any view size
 */
export interface AbstractDecorationElement {
  type: 'image' | 'shape' | 'gradient-shape';

  /** Anchor point determines how element is positioned */
  anchor: AnchorPoint;

  /** Width specification */
  width: SizeSpec;

  /** Height specification */
  height: SizeSpec;

  /** Offset from anchor point (in pixels, scaled proportionally) */
  offset?: { x: number; y: number };

  /** Reference size these offsets were designed for (defaults to STANDARD) */
  referenceSize?: ViewSize;

  // Shape properties
  shapeType?: 'rectangle' | 'ellipse' | 'roundedRectangle' | 'isoscelesTriangle' | 'rightTriangle';
  color?: string;
  rotation?: 0 | 1 | 2 | 3;
  /** Corner radius for rounded shapes (pixels) */
  cornerRadius?: number;

  // Border properties (applies to shapes, images, and media objects)
  /** Border definition */
  border?: BorderDef;

  // Image properties
  imageUrl?: string;

  // Gradient properties
  gradient?: GradientDef;
}

/**
 * Abstract screen decoration using the auto-conform system
 */
export interface AbstractScreenDecoration {
  appliesTo?: ScreenType[];
  elements: AbstractDecorationElement[];
}

/**
 * Helper to create a percentage size spec
 */
export function pct(value: number): SizeSpec {
  return { value, unit: '%' };
}

/**
 * Helper to create a pixel size spec
 */
export function px(value: number): SizeSpec {
  return { value, unit: 'px' };
}

/**
 * Conform an abstract decoration element to a concrete frame at the target view size
 */
export function conformDecoration(
  element: AbstractDecorationElement,
  targetSize: ViewSize
): DecorationElement {
  const refSize = element.referenceSize || VIEW_SIZES.STANDARD;

  // Calculate actual dimensions
  const width = resolveSizeSpec(element.width, targetSize.width, refSize.width);
  const height = resolveSizeSpec(element.height, targetSize.height, refSize.height);

  // Scale offsets proportionally
  const scaleX = targetSize.width / refSize.width;
  const scaleY = targetSize.height / refSize.height;
  const offsetX = (element.offset?.x || 0) * scaleX;
  const offsetY = (element.offset?.y || 0) * scaleY;

  // Calculate position based on anchor
  const { x, y } = calculateAnchorPosition(
    element.anchor,
    targetSize,
    width,
    height,
    offsetX,
    offsetY
  );

  // Build the concrete decoration element
  const concrete: DecorationElement = {
    type: element.type,
    frame: {
      x: Math.round(x),
      y: Math.round(y),
      width: Math.round(width),
      height: Math.round(height),
    },
  };

  if (element.shapeType) concrete.shapeType = element.shapeType;
  if (element.color) concrete.color = element.color;
  if (element.rotation !== undefined) concrete.rotation = element.rotation;
  if (element.cornerRadius !== undefined) concrete.cornerRadius = element.cornerRadius;
  if (element.border) concrete.border = element.border;
  if (element.imageUrl) concrete.imageUrl = element.imageUrl;
  if (element.gradient) concrete.gradient = element.gradient;

  return concrete;
}

/**
 * Resolve a size spec to actual pixels
 */
function resolveSizeSpec(spec: SizeSpec, targetDimension: number, refDimension: number): number {
  if (spec.unit === '%') {
    return (spec.value / 100) * targetDimension;
  }
  // Pixel values scale proportionally from reference size
  return spec.value * (targetDimension / refDimension);
}

/**
 * Calculate x,y position based on anchor point
 */
function calculateAnchorPosition(
  anchor: AnchorPoint,
  viewSize: ViewSize,
  width: number,
  height: number,
  offsetX: number,
  offsetY: number
): { x: number; y: number } {
  let x = 0;
  let y = 0;

  switch (anchor) {
    // Top row
    case 'top-left':
      x = offsetX;
      y = offsetY;
      break;
    case 'top-center':
      x = (viewSize.width - width) / 2 + offsetX;
      y = offsetY;
      break;
    case 'top-right':
      x = viewSize.width - width + offsetX;
      y = offsetY;
      break;

    // Center row
    case 'center-left':
      x = offsetX;
      y = (viewSize.height - height) / 2 + offsetY;
      break;
    case 'center':
      x = (viewSize.width - width) / 2 + offsetX;
      y = (viewSize.height - height) / 2 + offsetY;
      break;
    case 'center-right':
      x = viewSize.width - width + offsetX;
      y = (viewSize.height - height) / 2 + offsetY;
      break;

    // Bottom row
    case 'bottom-left':
      x = offsetX;
      y = viewSize.height - height + offsetY;
      break;
    case 'bottom-center':
      x = (viewSize.width - width) / 2 + offsetX;
      y = viewSize.height - height + offsetY;
      break;
    case 'bottom-right':
      x = viewSize.width - width + offsetX;
      y = viewSize.height - height + offsetY;
      break;

    // Stretch modes (position at 0,0 - width/height handle sizing)
    case 'stretch':
    case 'stretch-horizontal':
    case 'stretch-vertical':
      x = offsetX;
      y = offsetY;
      break;
  }

  return { x, y };
}

/**
 * Conform an entire abstract screen decoration to a target view size
 */
export function conformScreenDecoration(
  decoration: AbstractScreenDecoration,
  targetSize: ViewSize
): ScreenDecoration {
  return {
    appliesTo: decoration.appliesTo,
    elements: decoration.elements.map(el => conformDecoration(el, targetSize)),
  };
}

/**
 * Conform all abstract decorations for a theme to a target view size
 */
export function conformThemeDecorations(
  decorations: AbstractScreenDecoration[],
  targetSize: ViewSize
): ScreenDecoration[] {
  return decorations.map(dec => conformScreenDecoration(dec, targetSize));
}

/**
 * Get decorations for a screen, with auto-conform support.
 * Accepts either legacy ScreenDecoration[] or new AbstractScreenDecoration[].
 *
 * Note: 'toc' screen type falls back to 'content' decorations.
 */
export function getDecorationsForScreenWithConform(
  decorations: (ScreenDecoration | AbstractScreenDecoration)[] | undefined,
  screenType: ScreenType,
  targetSize: ViewSize = VIEW_SIZES.STANDARD
): DecorationElement[] {
  if (!decorations || decorations.length === 0) {
    return [];
  }

  // TOC screens use 'content' decorations as fallback
  const effectiveScreenType = screenType === 'toc' ? 'content' : screenType;

  const elements: DecorationElement[] = [];

  for (const dec of decorations) {
    // Check if this is an abstract decoration (has AbstractDecorationElement with anchor)
    const isAbstract = dec.elements.length > 0 && 'anchor' in dec.elements[0];

    const shouldInclude = !dec.appliesTo || dec.appliesTo.includes(effectiveScreenType);
    if (!shouldInclude) continue;

    if (isAbstract) {
      // Conform abstract elements to target size
      const conformed = (dec as AbstractScreenDecoration).elements.map(
        el => conformDecoration(el, targetSize)
      );
      elements.push(...conformed);
    } else {
      // Legacy format - scale proportionally from reference size (1024x748)
      const scaled = (dec as ScreenDecoration).elements.map(
        el => scaleLegacyDecoration(el, VIEW_SIZES.STANDARD, targetSize)
      );
      elements.push(...scaled);
    }
  }

  return elements;
}

/**
 * Scale a legacy decoration element from reference size to target size.
 * Intelligently handles different scaling scenarios:
 * - Full-width elements stretch to full target width
 * - Full-height elements stretch to full target height
 * - Other elements scale proportionally
 */
export function scaleLegacyDecoration(
  element: DecorationElement,
  refSize: ViewSize,
  targetSize: ViewSize
): DecorationElement {
  const frame = element.frame;
  const scaleX = targetSize.width / refSize.width;
  const scaleY = targetSize.height / refSize.height;

  // Detect full-width elements (within 2px tolerance)
  const isFullWidth = Math.abs(frame.width - refSize.width) < 2;
  // Detect full-height elements
  const isFullHeight = Math.abs(frame.height - refSize.height) < 2;
  // Detect elements anchored to bottom (y + height near ref height)
  const isBottomAnchored = Math.abs((frame.y + frame.height) - refSize.height) < 40;
  // Detect elements anchored to right
  const isRightAnchored = Math.abs((frame.x + frame.width) - refSize.width) < 40;

  let newX = frame.x * scaleX;
  let newY = frame.y * scaleY;
  let newWidth = frame.width * scaleX;
  let newHeight = frame.height * scaleY;

  // Full-width elements: stretch to full target width
  if (isFullWidth) {
    newX = 0;
    newWidth = targetSize.width;
  }

  // Full-height elements: stretch to full target height
  if (isFullHeight) {
    newY = 0;
    newHeight = targetSize.height;
  }

  // Bottom-anchored elements: keep consistent distance from bottom
  if (isBottomAnchored && !isFullHeight) {
    const distanceFromBottom = refSize.height - (frame.y + frame.height);
    newY = targetSize.height - newHeight - (distanceFromBottom * scaleY);
  }

  // Right-anchored elements: keep consistent distance from right
  if (isRightAnchored && !isFullWidth) {
    const distanceFromRight = refSize.width - (frame.x + frame.width);
    newX = targetSize.width - newWidth - (distanceFromRight * scaleX);
  }

  return {
    ...element,
    frame: {
      x: Math.round(newX),
      y: Math.round(newY),
      width: Math.round(newWidth),
      height: Math.round(newHeight),
    },
  };
}

// =============================================================================
// DECORATION BUILDER HELPERS
// =============================================================================
// Convenient functions for creating common decoration patterns

/**
 * Create a full-screen background shape
 */
export function fullScreenBackground(color: string): AbstractDecorationElement {
  return {
    type: 'shape',
    anchor: 'stretch',
    width: pct(100),
    height: pct(100),
    shapeType: 'rectangle',
    color,
  };
}

/**
 * Create a full-screen gradient background
 */
export function fullScreenGradient(gradient: GradientDef): AbstractDecorationElement {
  return {
    type: 'gradient-shape',
    anchor: 'stretch',
    width: pct(100),
    height: pct(100),
    shapeType: 'rectangle',
    gradient,
  };
}

/**
 * Create a bottom accent bar (full width, fixed height)
 */
export function bottomAccentBar(color: string, heightPx: number = 38): AbstractDecorationElement {
  return {
    type: 'shape',
    anchor: 'bottom-left',
    width: pct(100),
    height: px(heightPx),
    shapeType: 'rectangle',
    color,
  };
}

/**
 * Create a top accent bar (full width, fixed height)
 */
export function topAccentBar(color: string, heightPx: number = 38): AbstractDecorationElement {
  return {
    type: 'shape',
    anchor: 'top-left',
    width: pct(100),
    height: px(heightPx),
    shapeType: 'rectangle',
    color,
  };
}

/**
 * Create a left sidebar (fixed width, full height)
 */
export function leftSidebar(color: string, widthPx: number = 80): AbstractDecorationElement {
  return {
    type: 'shape',
    anchor: 'top-left',
    width: px(widthPx),
    height: pct(100),
    shapeType: 'rectangle',
    color,
  };
}

/**
 * Create a corner accent shape
 */
export function cornerAccent(
  corner: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right',
  color: string,
  sizePx: number = 150
): AbstractDecorationElement {
  const anchorMap: Record<string, AnchorPoint> = {
    'top-left': 'top-left',
    'top-right': 'top-right',
    'bottom-left': 'bottom-left',
    'bottom-right': 'bottom-right',
  };
  return {
    type: 'shape',
    anchor: anchorMap[corner],
    width: px(sizePx),
    height: px(sizePx),
    shapeType: 'ellipse',
    color,
  };
}
